import { Injectable, inject } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FeedbackCreateRequest } from '../types/feedback-api.types';
import { FeedbackPayload } from '../types/feedback-payload';

export interface PayloadValidationResult {
  valid: boolean;
  errors: readonly string[];
}

const MAX_TITLE_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 5000;
const LETTER_PATTERN = /\p{L}/u;

function hasAlphabeticCharacter(value: string): boolean {
  return LETTER_PATTERN.test(value);
}

export function meaningfulTextValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();

    return hasAlphabeticCharacter(value) ? null : { meaningfulText: true };
  };
}

@Injectable({
  providedIn: 'root',
})
export class FeedbackPayloadValidatorService {
  private readonly translate = inject(TranslateService);

  validateFeedbackPayload(payload: FeedbackPayload | FeedbackCreateRequest | null | undefined): PayloadValidationResult {
    const errors: string[] = [];

    if (!payload) {
      return { valid: false, errors: [this.translate.instant('feedbackWidget.validation.payloadMissing')] };
    }

    const title = payload.title.trim();
    if (!title) {
      errors.push(this.translate.instant('feedbackWidget.validation.titleRequired'));
    } else if (title.length > MAX_TITLE_LENGTH) {
      errors.push(this.translate.instant('feedbackWidget.validation.titleMaxLength', { max: MAX_TITLE_LENGTH }));
    } else if (!hasAlphabeticCharacter(title)) {
      errors.push(this.translate.instant('feedbackWidget.details.titleMeaningfulTextError'));
    }

    const description = payload.description.trim();
    if (!description) {
      errors.push(this.translate.instant('feedbackWidget.validation.descriptionRequired'));
    } else if (description.length > MAX_DESCRIPTION_LENGTH) {
      errors.push(this.translate.instant('feedbackWidget.validation.descriptionMaxLength', { max: MAX_DESCRIPTION_LENGTH }));
    } else if (!hasAlphabeticCharacter(description)) {
      errors.push(this.translate.instant('feedbackWidget.details.descriptionMeaningfulTextError'));
    }

    if (!payload.category.trim()) {
      errors.push(this.translate.instant('feedbackWidget.validation.categoryRequired'));
    }

    if (!payload.priority?.trim()) {
      errors.push(this.translate.instant('feedbackWidget.validation.priorityRequired'));
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}
